import STLogo from "@/components/STLogo";
import { Mail } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

export default function Footer() {
  const { t } = useI18n();
  const f = t.footer;
  const nav = [
    { key: "about", href: "/#about" },
    { key: "services", href: "/#services" },
    { key: "products", href: "/products" },
    { key: "roadmap", href: "/#roadmap" },
    { key: "process", href: "/#process" },
    { key: "leadership", href: "/#leadership" },
    { key: "contact", href: "/#contact" },
  ];

  return (
    <footer data-testid="site-footer" className="bg-surface-dark text-white relative overflow-hidden">
      <div className="absolute inset-0 dot-bg opacity-30" aria-hidden />
      <div className="absolute inset-0 noise-overlay pointer-events-none" aria-hidden />
      <div className="container-pro relative pt-16 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            {/* logo on white chip so brand colors stay true on dark */}
            <span className="inline-flex items-center rounded-lg bg-white px-3 py-2">
              <STLogo />
            </span>
            <p className="mt-5 text-sm text-white/60 max-w-sm">{f.blurb}</p>
            <a
              href="mailto:info@smartterminals.co"
              className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-white/80 hover:text-brand-red transition-colors"
              data-testid="footer-email"
            >
              <Mail size={16} /> info@smartterminals.co
            </a>
          </div>

          <div className="md:col-span-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">{f.navigate}</div>
            <ul className="mt-4 space-y-2">
              {nav.map((n) => (
                <li key={n.href}>
                  <a
                    href={n.href}
                    className="group inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
                    data-testid={`footer-nav-${n.key}`}
                  >
                    <span className="h-px w-0 bg-brand-red transition-all duration-300 group-hover:w-4" aria-hidden />
                    {t.nav[n.key]}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Products</div>
            <ul className="mt-4 space-y-2.5">
              <li>
                <a href="/products/smarterp" className="block group" data-testid="footer-product-smarterp">
                  <div className="text-sm font-medium text-white group-hover:text-brand-red transition-colors">SmartERP →</div>
                  <div className="text-xs text-white/40">Cloud-native multi-tenant ERP</div>
                </a>
              </li>
              <li>
                <a href="/products/finance-st" className="block group" data-testid="footer-product-finance">
                  <div className="text-sm font-medium text-white group-hover:text-brand-red transition-colors">Finance-ST →</div>
                  <div className="text-xs text-white/40">AI-driven CFO intelligence</div>
                </a>
              </li>
            </ul>

            <div className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">{f.servicesLabel}</div>
            <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5">
              {f.services.map((s) => (
                <li key={s} className="text-xs text-white/50">{s}</li>
              ))}
            </ul>
          </div>
        </div>

        <div
          aria-hidden
          className="mt-16 pointer-events-none select-none font-heading font-bold tracking-tighter text-outline-dark leading-none"
          style={{ fontSize: "clamp(48px, 12vw, 200px)" }}
        >
          {f.tagline}
        </div>

        <div className="mt-8 pt-6 border-t border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-xs text-white/40">© {new Date().getFullYear()} Smart Terminals (ST). {f.rights}</p>
          <p className="text-xs text-white/40 font-mono">{f.alignment}</p>
        </div>
      </div>
    </footer>
  );
}
